import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const AboutSection = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const pillars = [
    { icon: "ri-bank-line", label: "決済管理", sub: "Payment Mgmt" },
    { icon: "ri-funds-line", label: "資金管理", sub: "Fund Mgmt" },
    { icon: "ri-code-s-slash-line", label: "システム開発", sub: "System Dev" },
    { icon: "ri-bar-chart-2-line", label: "データ分析", sub: "Analytics" },
  ];

  return (
    <section ref={ref} className="relative bg-white overflow-hidden">
      {/* Diagonal separator from hero */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-[#020b18]"
        style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 lg:pb-32">
        {/* Section label */}
        <div
          className={`flex items-center gap-3 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <div className="w-1 h-12 bg-gradient-to-b from-cyan-500 to-transparent" />
          <div>
            <span className="text-cyan-600 text-xs font-bold tracking-[0.3em] uppercase block">{t("top.about.label")}</span>
            <span className="text-slate-300 text-xs tracking-widest">会社について</span>
          </div>
        </div>

        {/* Main layout: full-width headline + split content */}
        <div
          className={`transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-4xl lg:text-6xl xl:text-7xl font-black text-slate-900 leading-none mb-16 tracking-tight">
            {t("top.about.title")}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: image with diagonal clip */}
          <div
            className={`lg:col-span-5 transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}
          >
            <div
              className="relative h-80 lg:h-[420px] overflow-hidden rounded-3xl"
              style={{ clipPath: "polygon(0 0, 100% 0, 92% 100%, 0% 100%)" }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=modern-financial-technology-server-room-data-center-with-glowing-blue-cyan-lights-rows-of-servers-clean-professional-environment-dark-atmosphere-no-people-high-tech-infrastructure&width=700&height=600&seq=home_about_v2&orientation=portrait"
                alt="Stream Solutions technology"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-slate-900/40" />
            </div>

            {/* Floating stat card */}
            <div
              className={`relative -mt-12 ml-8 inline-block transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            >
              <div className="bg-slate-900 text-white rounded-2xl px-6 py-4 border border-slate-700">
                <div className="text-3xl font-black text-cyan-400">10+</div>
                <div className="text-xs text-slate-400 mt-0.5">Years of Excellence</div>
              </div>
            </div>
          </div>

          {/* Right: text + pillars */}
          <div
            className={`lg:col-span-7 transition-all duration-1000 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}
          >
            <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
              {t("top.about.desc")}
            </p>

            {/* Pillars: horizontal scroll on mobile, 2x2 on desktop */}
            <div className="grid grid-cols-2 gap-3 mb-10">
              {pillars.map((p) => (
                <div
                  key={p.label}
                  className="group flex items-center gap-4 p-4 rounded-2xl border border-slate-100 hover:border-cyan-200 hover:bg-cyan-50/50 transition-all duration-300 cursor-default"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-900 group-hover:bg-cyan-500 transition-colors duration-300 flex-shrink-0">
                    <i className={`${p.icon} text-cyan-400 group-hover:text-white text-base transition-colors duration-300`} />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{p.label}</div>
                    <div className="text-xs text-slate-400">{p.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-3 px-7 py-3.5 bg-slate-900 text-white font-bold rounded-full hover:bg-cyan-600 transition-all duration-300 cursor-pointer whitespace-nowrap group text-sm"
            >
              {t("top.about.btn")}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
